/**
 * Section A3 Page
 * A.3.政策符合性說明
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import sectionA3Service from '../services/sectionA3Service';
import { SectionA3 } from '../types/section_a3';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const SectionA3Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  // ========== 資料狀態 ==========
  const [sectionA3Data, setSectionA3Data] = useState<SectionA3 | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ========== 表單狀態 ==========
  const [formData, setFormData] = useState({
    project_policy_alignment: ''
  });

  // ========== 初始化 ==========
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('SectionA3Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // ========== 專案變更時載入資料 ==========
  useEffect(() => {
    if (!selectedProjectId) {
      setSectionA3Data(null);
      setFormData({ project_policy_alignment: '' });
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(null);
        const data = await sectionA3Service.getSectionA3ByProject(selectedProjectId);
        if (data) {
          setSectionA3Data(data);
          setFormData({ project_policy_alignment: data.project_policy_alignment || '' });
        } else {
          setSectionA3Data(null);
          setFormData({ project_policy_alignment: '' });
        }
      } catch (err: any) {
        console.error('載入 A.3 資料失敗:', err);
        setError(err.response?.data?.detail || '載入資料失敗');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProjectId]);

  // ========== 儲存 ==========
  const handleSave = async () => {
    if (!selectedProjectId) {
      setError(i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const updatedData = await sectionA3Service.updateSectionA3(selectedProjectId, {
        project_policy_alignment: formData.project_policy_alignment || undefined
      });

      setSectionA3Data(updatedData);
      setSuccess(i18n.language === 'zh-TW' ? '儲存成功' : 'Saved successfully');
      toast.success(i18n.language === 'zh-TW' ? '儲存成功' : 'Saved successfully');

    } catch (err: any) {
      console.error('儲存失敗:', err);
      const errorMsg = err.response?.data?.detail || '儲存失敗';
      setError(errorMsg);
      toast.error(i18n.language === 'zh-TW' ? `儲存失敗: ${errorMsg}` : `Save failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // ========== 權限檢查 ==========
  if (permissionLoading) {
    return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (!hasPermission('section_a3', 'read')) {
    return (
      <div className="page-container">
        <div className="error-message">{t('common.noPermission')}</div>
      </div>
    );
  }

  const canEdit = hasPermission('section_a3', 'update');

  // ========== 渲染 ==========
  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_a3" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}
      {success && <div className="success-message" style={{ marginBottom: '16px' }}>{success}</div>}

      {selectedProjectId && (
        <div style={{ marginBottom: '24px' }}>
          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : (
            <>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  {i18n.language === 'zh-TW' ? '政策符合說明' : 'Policy Alignment Description'}
                </label>
                <RichTextEditor
                  value={formData.project_policy_alignment}
                  onChange={(html) => setFormData({ ...formData, project_policy_alignment: html })}
                  placeholder={i18n.language === 'zh-TW' ? '請輸入政策符合說明...' : 'Please enter policy alignment description...'}
                  disabled={!canEdit}
                />
              </div>

              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading}>
                    {loading ? (i18n.language === 'zh-TW' ? '儲存中...' : 'Saving...') : (i18n.language === 'zh-TW' ? '儲存' : 'Save')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!selectedProjectId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project from the top bar'}
        </div>
      )}
    </div>
  );
};

export default SectionA3Page;
